import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';

const ProductForm = ({ onSave, onCancel, initialData, isEditing }) => {
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    description: '',
    price: '',
    stock: '',
    category: 'pijama', // Valor padrão
    images: []
  });
  
  const [selectedImages, setSelectedImages] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Quando o componente recebe dados iniciais (para edição)
  useEffect(() => {
    if (initialData) {
      setFormData({
        ...initialData,
        price: initialData.price ? String(initialData.price) : '',
        stock: initialData.stock ? String(initialData.stock) : ''
      });
    }
  }, [initialData]);
  
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Nome do produto é obrigatório';
    }
    
    if (!formData.description.trim()) {
      newErrors.description = 'Descrição do produto é obrigatória';
    }
    
    if (!formData.price.trim()) {
      newErrors.price = 'Preço é obrigatório';
    } else if (isNaN(parseFloat(formData.price)) || parseFloat(formData.price) <= 0) {
      newErrors.price = 'Preço deve ser um número válido maior que zero';
    }
    
    if (formData.stock.trim() && (isNaN(parseInt(formData.stock)) || parseInt(formData.stock) < 0)) {
      newErrors.stock = 'Estoque deve ser um número válido não negativo';
    }
    
    if (!isEditing && selectedImages.length === 0 && (!formData.images || formData.images.length === 0)) {
      newErrors.images = 'Pelo menos uma imagem é necessária';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleImageSelect = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    const fileArray = Array.from(e.target.files).map(file => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name
    }));
    
    setSelectedImages(prev => [...prev, ...fileArray]);
  };
  
  const removeSelectedImage = (index) => {
    setSelectedImages(prev => {
      const newImages = [...prev];
      // Revoke object URL to prevent memory leaks
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };
  
  const removeExistingImage = (index) => {
    setFormData(prev => {
      const newImages = [...prev.images];
      newImages.splice(index, 1);
      return { ...prev, images: newImages };
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Preparar dados para envio
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        stock: formData.stock ? parseInt(formData.stock) : 0,
      };
      
      // Processar imagens selecionadas
      // Em um app real, aqui você faria upload das imagens para um servidor
      // Para este exemplo, vamos simular convertendo-as para data URLs
      if (selectedImages.length > 0) {
        const imagePromises = selectedImages.map(img => {
          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => {
              resolve({ 
                url: reader.result,
                name: img.name 
              });
            };
            reader.readAsDataURL(img.file);
          });
        });
        
        const processedImages = await Promise.all(imagePromises);
        
        // Combinar imagens existentes com novas imagens
        productData.images = [
          ...(productData.images || []),
          ...processedImages
        ];
      }
      
      onSave(productData);
    } catch (error) {
      console.error('Erro ao processar formulário:', error);
      setErrors({ form: 'Erro ao processar o formulário. Tente novamente.' });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Limpar object URLs ao desmontar o componente
  useEffect(() => {
    return () => {
      selectedImages.forEach(img => URL.revokeObjectURL(img.preview));
    };
  }, [selectedImages]);
  
  return (
    <motion.div
      className="admin-form"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
    >
      <form onSubmit={handleSubmit}>
        {errors.form && (
          <div className="error-message">{errors.form}</div>
        )}
        
        <div className="form-group">
          <label htmlFor="name">Nome do Produto</label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="Digite o nome do produto"
            autoFocus
          />
          {errors.name && <small className="error">{errors.name}</small>}
        </div>
        
        <div className="form-group">
          <label htmlFor="description">Descrição</label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="Digite a descrição do produto"
            rows={4}
          />
          {errors.description && <small className="error">{errors.description}</small>}
        </div>
        
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="price">Preço (R$)</label>
            <input
              type="text"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              placeholder="0.00"
            />
            {errors.price && <small className="error">{errors.price}</small>}
          </div>
          
          <div className="form-group">
            <label htmlFor="stock">Estoque</label>
            <input
              type="text"
              id="stock"
              name="stock"
              value={formData.stock}
              onChange={handleChange}
              placeholder="Quantidade em estoque"
            />
            {errors.stock && <small className="error">{errors.stock}</small>}
          </div>
        </div>
        
        <div className="form-group">
          <label htmlFor="category">Categoria</label>
          <select
            id="category"
            name="category"
            value={formData.category}
            onChange={handleChange}
          >
            <option value="pijama">Pijama</option>
            <option value="camisola">Camisola</option>
            <option value="robe">Robe</option>
            <option value="roupao">Roupão</option>
            <option value="acessorio">Acessório</option>
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor="images">Imagens</label>
          <input
            type="file"
            id="images"
            name="images"
            onChange={handleImageSelect}
            accept="image/*"
            multiple
          />
          <small>Selecione uma ou mais imagens para o produto</small>
          {errors.images && <small className="error">{errors.images}</small>}
          
          {/* Exibir imagens existentes */}
          {formData.images && formData.images.length > 0 && (
            <>
              <div className="form-subheader">Imagens existentes:</div>
              <div className="image-previews">
                {formData.images.map((image, index) => (
                  <div key={`existing-${index}`} className="image-preview">
                    <img src={image.url} alt={`Produto ${index}`} />
                    <button 
                      type="button" 
                      className="remove-button" 
                      onClick={() => removeExistingImage(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
          
          {/* Exibir imagens selecionadas */}
          {selectedImages.length > 0 && (
            <>
              <div className="form-subheader">Novas imagens:</div>
              <div className="image-previews">
                {selectedImages.map((image, index) => (
                  <div key={`new-${index}`} className="image-preview">
                    <img src={image.preview} alt={`Nova imagem ${index}`} />
                    <button 
                      type="button" 
                      className="remove-button" 
                      onClick={() => removeSelectedImage(index)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        
        <div className="form-actions">
          <button
            type="button"
            className="admin-button secondary"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className={`admin-button ${isSubmitting ? 'loading' : ''}`}
            disabled={isSubmitting}
          >
            {isEditing ? 'Atualizar' : 'Adicionar'} Produto
          </button>
        </div>
      </form>
    </motion.div>
  );
};

export default ProductForm; 